import { TRPCClientError } from '@trpc/client'

const SERVER_ERROR_CODES = new Set(['BAD_REQUEST', 'UNAUTHORIZED', 'FORBIDDEN'])

// True when the failure looks like a connectivity problem (worth retrying /
// falling back to the offline path). False when the server definitively
// rejected the request — replaying it would fail forever.
export function isNetworkError(err: unknown): boolean {
  if (!(err instanceof TRPCClientError)) return false
  const code = (err.data as { code?: string } | null | undefined)?.code
  return !code || !SERVER_ERROR_CODES.has(code)
}

// True when the failure is a pure transport error (no server response at all).
// Transport failures should NOT count toward the retry cap — the server never
// received the request so there is no risk of repeated side-effects.
export function isTransportError(err: unknown): boolean {
  if (!(err instanceof TRPCClientError)) return true
  const code = (err.data as { code?: string } | null | undefined)?.code
  return !code
}

// True when the error is specifically UNAUTHORIZED (HTTP 401 / expired token).
// These must NOT dead-letter — the token may be refreshed. Abort the flush loop
// instead so all pending items remain retryable after re-auth.
export function isUnauthorizedError(err: unknown): boolean {
  if (!(err instanceof TRPCClientError)) return false
  const code = (err.data as { code?: string } | null | undefined)?.code
  return code === 'UNAUTHORIZED'
}

// ── User-facing error messages ───────────────────────────────────────────────
// Raw SDK / transport error strings ("NetworkError when attempting to fetch
// resource.") must never reach the UI. Every error shown to the user goes
// through one of the two mappers below; the raw error is logged to the console
// for debugging instead.

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'
export const CONNECTION_ERROR_MESSAGE = "Can't connect right now. Check your internet and try again."
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

function looksLikeTransportMessage(msg: string): boolean {
  return msg.includes('network') || msg.includes('fetch') || msg.includes('load failed') || msg.includes('connection')
}

// Maps Supabase auth SDK errors (auth pages call GoTrue directly) to plain
// language. Anything unrecognized falls back to a generic line.
export function friendlyAuthError(error: { message?: string; status?: number } | null | undefined): string {
  const raw = error?.message ?? ''
  const msg = raw.toLowerCase()
  console.error('[auth]', error)
  if (!msg || looksLikeTransportMessage(msg)) return CONNECTION_ERROR_MESSAGE
  if (error?.status === 429 || msg.includes('rate limit') || msg.includes('too many')) {
    return 'Too many attempts. Wait a minute and try again.'
  }
  if (msg.includes('invalid login credentials')) return 'Wrong email or password.'
  if (msg.includes('email not confirmed')) return 'Confirm your email first — check your inbox for the link.'
  if (msg.includes('already registered')) return 'This email already has an account. Try signing in instead.'
  if (msg.includes('should be different')) return 'New password must be different from your current one.'
  if (msg.includes('password should')) return raw // GoTrue password-policy messages are already plain language
  if (msg.includes('expired') || msg.includes('invalid token') || msg.includes('otp')) {
    return 'This link has expired. Request a new one.'
  }
  return GENERIC_ERROR_MESSAGE
}

// tRPC error codes whose messages our backend writes for end users.
// INTERNAL_SERVER_ERROR is deliberately excluded — unexpected 500s can carry
// raw upstream messages.
const USER_FACING_CODES = new Set([
  'BAD_REQUEST',
  'CONFLICT',
  'NOT_FOUND',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'TOO_MANY_REQUESTS',
  'PRECONDITION_FAILED',
])

// Maps a caught tRPC mutation/query error to a message safe to show users.
// Backend TRPCError messages on user-facing codes pass through; transport
// failures become a connectivity line; everything else becomes `fallback`.
export function friendlyMutationError(err: unknown, fallback = GENERIC_ERROR_MESSAGE): string {
  if (!(err instanceof TRPCClientError)) {
    console.error('[mutation]', err)
    return fallback
  }
  const code = (err.data as { code?: string } | null | undefined)?.code
  if (!code) return CONNECTION_ERROR_MESSAGE // pure transport failure — message is a raw fetch error
  if (!USER_FACING_CODES.has(code)) {
    console.error('[mutation]', err)
    return fallback
  }
  const msg = err.message
  // TRPCError thrown without a message defaults its message to the code name.
  if (!msg || msg === code) return code === 'UNAUTHORIZED' ? SESSION_EXPIRED_MESSAGE : fallback
  // Zod input validation failures arrive as a JSON-stringified issue array.
  if (msg.startsWith('[')) {
    try {
      const issues = JSON.parse(msg) as { message?: unknown }[]
      const first = issues.find(i => typeof i.message === 'string' && i.message)?.message
      if (typeof first === 'string') return first
    } catch { /* not JSON — fall through */ }
    return fallback
  }
  return msg
}
